/* global RequestInfo, RequestInit */
import { ApolloClient, ApolloLink, InMemoryCache, from, split } from '@apollo/client';
import { ReadFieldFunction } from '@apollo/client/cache/core/types/common';
import type { KeyFieldsContext } from '@apollo/client/cache/inmemory/policies';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { HttpLink } from '@apollo/client/link/http';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { StoreObject, getMainDefinition } from '@apollo/client/utilities';
import { get, isEmpty, isNil, isUndefined } from 'lodash';
import possibleTypes from '@shared/api/possibleTypes.json';
import { ConnectivityNotifier } from '@shared/scenes/connection/connectivity';
import { MBSentry } from '@shared/util/sentry';
import { createWebSocketLink } from './client/WebSocketLink';
import {
  authLink,
  customRequestHeadersLink,
  errorLoggerLink,
  removeCustomCacheKeyLink,
  removeTypenameFromMutationLink,
  stampWorkflowTemplateIdLink,
} from './customApolloLinks';

/*
    In shared components, we want to be able to run GQL queries that
    aren't attached directly to components. In web/native we only
    rely on react-apollo's HOCs for mutations and queries. This makes
    reusability hard. Therefore we expose the actual apollo client
    to be used in dispatch actions.
*/
let apolloClient: ApolloClient<unknown> | undefined = undefined;

export let webSocketLink: GraphQLWsLink | undefined = undefined;

export function reconnectWebsocket() {
  webSocketLink?.client.terminate(); // non-fatal closing will attempt to be re-opened
}

/*
    To use the apollo client, we need to get it supplied by where it
    is configured in web/native. We add auth headers etc there and
    want to use the exact same client as is being used there. This
    semi-hack is a way to "send" that client object over into the
    shared component world so that we can use it freely here.
    Note: this is expected to be called before apolloClient (above)
    is used by shared components.
*/
export function setSharedApolloClient(client: ApolloClient<unknown>) {
  apolloClient = client;
}

export function getSharedApolloClient(): ApolloClient<unknown> {
  if (!apolloClient) {
    throw new Error('Apollo client not initialized');
  }
  return apolloClient;
}

/**
 * Component types normalized with composite keys using workflowTemplateId + id.
 * Single source of truth is WORKFLOW_COMPONENT_TYPENAMES.
 */

export function createApolloClient(
  responseHeadersLinks: ApolloLink[] = [],
  connectivityNotifier: ConnectivityNotifier,
  customRequestHeaders: () => Promise<Record<string, string>> = async () => ({}),
): ApolloClient<unknown> {
  const queryLoggerLink = new ApolloLink((operation, forward) => {
    MBSentry.breadcrumbFromApolloOperation(operation);
    return forward(operation);
  });

  webSocketLink = createWebSocketLink(connectivityNotifier);

  const fetchNotifyOffline = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const p = fetch(input, init);
    p.catch(reason => {
      if (reason.name !== 'AbortError') {
        connectivityNotifier.offline('fetch::error');
      }
    });
    return p;
  };

  const uri = `${import.meta.env.VITE_API_URL}/api/graphql`;
  const httpLink = new HttpLink({ uri, fetch: fetchNotifyOffline });
  const batchHttpLink = new BatchHttpLink({ uri });

  const queriesAndMutationsLink = split(
    operation => operation.operationName === 'ListCardsQuery',
    batchHttpLink,
    httpLink,
  );

  // Split queries based on if it's a subscription or not.
  // This is copy pasted from the Apollo docs:
  // https://www.apollographql.com/docs/react/advanced/subscriptions/#client-setup
  const dataLink = split(
    // split based on operation type
    operation => {
      const def = getMainDefinition(operation.query);
      return def.kind === 'OperationDefinition' && def.operation === 'subscription';
    },
    webSocketLink,
    queriesAndMutationsLink,
  );

  apolloClient = new ApolloClient({
    link: from([
      queryLoggerLink,
      customRequestHeadersLink(customRequestHeaders),
      authLink(),
      // Note(fant): this error link needs to be before the data link
      // https://stackoverflow.com/a/55169771
      errorLoggerLink,
      removeTypenameFromMutationLink,
      removeCustomCacheKeyLink,
      stampWorkflowTemplateIdLink,
      ...responseHeadersLinks,
      dataLink,
    ]),
    assumeImmutableResults: true,
    cache: new InMemoryCache({
      possibleTypes,
      // https://www.apollographql.com/docs/react/local-state/managing-state-with-field-policies/
      typePolicies: {
        Channel: {
          keyFields: (obj: Readonly<StoreObject>, context: Readonly<KeyFieldsContext>) => {
            if (!isNil(obj.slug)) {
              return `${context.typename}:${obj.slug}`;
            }
            if (import.meta.env.DEV) {
              console.warn(
                'Channel object missing `slug` for cache key. Ensure the query includes `slug`.',
              );
            }
            return false;
          },
          fields: {
            name: {
              merge(existing, incoming, options) {
                return isNil(existing) || !isCoreApiGetChannelQuery(options.variables)
                  ? incoming
                  : existing;
              },
            },
          },
        },
        Business: {
          fields: {
            img: {
              merge(existing, incoming, { readField }) {
                return incoming ?? readField('imageUrl') ?? existing ?? null;
              },
            },
            imageUrl: {
              merge(existing, incoming, { readField }) {
                return incoming ?? readField('img') ?? existing ?? null;
              },
            },
          },
        },
        ViewTemplate: {
          keyFields: ['workflowTemplateId', 'id'],
        },
        Query: {
          fields: {
            listViewTemplatesV2: {
              keyArgs: [
                'input',
                ['workflowTemplateId', 'type', 'subtype', 'isPublic', 'name', 'excludeSubtypes'],
              ],
              merge(existing, incoming, { readField, mergeObjects, args }) {
                return dedupeAndAdd({
                  existing,
                  incoming,
                  path: 'viewTemplates',
                  readField,
                  mergeObjects,
                  offset: args?.input.offset,
                });
              },
            },
            listReportsV2: {
              keyArgs: (args: Record<string, any> | null) => {
                const workflowTemplateIds = args?.input?.workflowTemplateIds;
                // Cache all reports together when not filtering by workflowTemplateIds
                if (isNil(workflowTemplateIds) || isEmpty(workflowTemplateIds)) {
                  return false;
                }
                // Cache separately when filtering by workflowTemplateIds
                return ['input', ['workflowTemplateIds']];
              },
              merge(existing, incoming, { readField, mergeObjects, args }) {
                return dedupeAndAdd({
                  existing,
                  incoming,
                  path: 'reports',
                  readField,
                  mergeObjects,
                  offset: args?.input.offset,
                });
              },
            },
            listPublicBundleListings: {
              keyArgs: ['input', ['query', 'tags']],
              merge(existing, incoming, { readField, mergeObjects, args }) {
                return dedupeAndAdd({
                  existing,
                  incoming,
                  path: 'bundleListings',
                  readField,
                  mergeObjects,
                  offset: args?.input.offset,
                });
              },
            },
            listFileOperations: {
              keyArgs: false,
              merge(existing, incoming, { readField, mergeObjects, args }) {
                return dedupeAndAdd({
                  existing,
                  incoming,
                  path: 'items',
                  readField,
                  mergeObjects,
                  offset: args?.input.offset,
                });
              },
            },
            listWorkflowTemplateAutomations: {
              keyArgs: ['input', ['workflowTemplateId', 'trigger', 'name']],
              merge(existing, incoming, { readField, mergeObjects, args }) {
                return dedupeAndAdd({
                  existing,
                  incoming,
                  path: 'automations',
                  readField,
                  mergeObjects,
                  offset: args?.input.offset,
                });
              },
            },
            listBusinesses: {
              keyArgs: ['input', ['name']],
              merge(existing, incoming, { readField, mergeObjects, args }) {
                return dedupeAndAdd({
                  existing,
                  incoming,
                  path: 'businesses',
                  readField,
                  mergeObjects,
                  offset: args?.input.offset,
                });
              },
            },
            listUsers: {
              keyArgs: (args: Record<string, any> | null, context: any) => {
                const customCacheKey = context.variables?.customCacheKey;
                if (!isNil(args) && !isEmpty(customCacheKey)) {
                  args.customCacheKey = customCacheKey;
                  return ['customCacheKey'];
                }
                return [
                  'input',
                  ['ids', 'name', 'isActive', 'isDeactivated', 'orderBys', 'excludeSystemUsers'],
                ];
              },
              merge(existing, incoming, { readField, mergeObjects, args }) {
                return dedupeAndAdd({
                  existing,
                  incoming,
                  path: 'users',
                  readField,
                  mergeObjects,
                  offset: args?.input.offset,
                });
              },
            },
            listWorkspaceSections: {
              keyArgs: false,
              merge(existing, incoming, { readField, mergeObjects, args }) {
                return dedupeAndAdd({
                  existing,
                  incoming,
                  path: 'channelAssociations',
                  readField,
                  mergeObjects,
                  offset: args?.input.offset,
                });
              },
            },
            listUserGroups: {
              keyArgs: ['input', ['ids', 'name']],
              merge(existing, incoming, { readField, mergeObjects, args }) {
                return dedupeAndAdd({
                  existing,
                  incoming,
                  path: 'userGroups',
                  readField,
                  mergeObjects,
                  offset: args?.input.offset,
                });
              },
            },
            listUserGroupUserAssociations: {
              keyArgs: ['input', ['userGroupId', 'userFirstOrLastName']],
              merge(existing, incoming, { readField, mergeObjects, args }) {
                return dedupeAndAdd({
                  existing,
                  incoming,
                  path: 'userGroupUserAssociations',
                  readField,
                  mergeObjects,
                  offset: args?.input.offset,
                });
              },
            },
            listCards: {
              keyArgs: [
                'input',
                [
                  'workflowTemplateId',
                  'relationFilters',
                  'legacyRelationFilters',
                  'channelId',
                  'startDateAfter',
                  'startDateBefore',
                  'order',
                  ['orderBy', 'orderDirection'],
                ],
              ],
              merge(existing, incoming, { readField, mergeObjects, args }) {
                // indicates query cache was adjusted externally, don't merge
                if (args?.input?.offset === 0) {
                  return incoming;
                }

                // paginated, go ahead and merge
                const merged = {
                  ...incoming,
                  cards: existing ? [...existing.cards] : [],
                };
                const cardIndexes: Record<string, number> = {};
                if (existing) {
                  existing.cards.forEach((card: StoreObject, index: number) => {
                    const id = readField<string>('id', card);
                    if (id) {
                      cardIndexes[id] = index;
                    }
                  });
                }
                incoming.cards.forEach((card: StoreObject) => {
                  const id = readField<string>('id', card);
                  if (id) {
                    const index = cardIndexes[id];
                    if (typeof index === 'number') {
                      merged.cards[index] = mergeObjects(merged.cards[index], card);
                    } else {
                      cardIndexes[id] = merged.cards.length;
                      merged.cards.push(card);
                    }
                  }
                });
                return merged;
              },
            },
            listWorkflowEntities: {
              keyArgs: [
                'input',
                ['workflowTemplateId', 'sortByComponents', 'filterByComponents', 'omniSearchQuery'],
              ],
              merge(existing, incoming, { readField, mergeObjects, args }) {
                // setting a limit of 0 is used to query for an updated count,
                // without affecting existing entities
                if (args?.input?.limit === 0) {
                  return {
                    ...existing,
                    totalResults: incoming.totalResults,
                  };
                }
                // initial page (empty or absent cursor): replace cache rather than merge
                if (!args?.input?.cursor) {
                  return incoming;
                }
                // paginated, go ahead and merge
                const merged = {
                  ...incoming,
                  entities: existing ? [...existing.entities] : [],
                  refs: {
                    __typename: 'EntityRefs',
                    personRefs: existing ? [...(existing.refs.personRefs || [])] : [],
                    workflowEntityRefs: existing
                      ? [...(existing.refs.workflowEntityRefs || [])]
                      : [],
                    workflowTemplateRefs: existing
                      ? [...(existing.refs.workflowTemplateRefs || [])]
                      : [],
                    recurringEntitiesScheduleRefs: existing
                      ? [...(existing.refs.recurringEntitiesScheduleRefs || [])]
                      : [],
                  },
                };

                // dedupe and add/merge
                const entityDedupeAndAdd = path => {
                  const indexes = {};
                  if (existing) {
                    const existingPath = get(existing, path);
                    existingPath?.forEach((card: StoreObject, index: number) => {
                      const id = readField<string>('id', card);
                      if (id) {
                        indexes[id] = index;
                      }
                    });
                  }
                  const incomingPath = get(incoming, path);
                  const mergedPath = get(merged, path);
                  incomingPath?.forEach((entity: StoreObject) => {
                    const id = readField<string>('id', entity);
                    if (id) {
                      const index = indexes[id];
                      if (typeof index === 'number') {
                        mergedPath[index] = mergeObjects(mergedPath[index], entity);
                      } else {
                        indexes[id] = mergedPath.length;
                        mergedPath.push(entity);
                      }
                    }
                  });
                };
                entityDedupeAndAdd('entities');
                entityDedupeAndAdd('refs.personRefs');
                entityDedupeAndAdd('refs.workflowEntityRefs');
                entityDedupeAndAdd('refs.workflowTemplateRefs');
                entityDedupeAndAdd('refs.recurringEntitiesScheduleRefs');
                return merged;
              },
            },
            listWorkflowTemplatesV2: {
              keyArgs: [
                'input',
                ['isPublic', 'isSubform', 'ids', 'name', 'relatedWorkflowTemplateId'],
              ],
              merge(existing, incoming, { readField, mergeObjects, args }) {
                return dedupeAndAdd({
                  existing,
                  incoming,
                  path: 'workflowTemplates',
                  readField,
                  mergeObjects,
                  offset: args?.input.offset,
                });
              },
            },
            // Embedded legacy payload: merge so refetches / partial writes never drop snoozeUntil.
            legacyGetNotificationSettings: {
              merge(existing, incoming, { mergeObjects }) {
                if (!incoming) {
                  return existing;
                }
                if (!existing) {
                  return incoming;
                }
                return mergeObjects(existing, incoming);
              },
            },
          },
        },
        ...createWorkflowComponentTypePolicies(possibleTypes.AnyComponent),
        // Disable caching for TodoComponentDataValue to avoid Apollo merging
        // todo items with the same id across different audit trail entries
        TodoComponentDataValue: {
          keyFields: false,
        },
      },
    }),
    queryDeduplication: true,
    defaultOptions: {
      query: { fetchPolicy: 'cache-first' },
    },
  });

  return apolloClient;
}

function isCoreApiGetChannelQuery(variables?: Record<string, any>): boolean {
  if (isNil(variables)) {
    return false;
  }
  const keys = Object.keys(variables);
  if (keys.length === 2 && keys.includes('slug') && keys.includes('id')) {
    return true;
  }
  return false;
}

type ListQueryResults = Record<string, any>;

interface DedupeAndAddProps {
  existing: ListQueryResults;
  incoming: ListQueryResults;
  path: string;
  readField: ReadFieldFunction;
  mergeObjects: (a: any, b: any) => any;
  offset?: number;
}

function dedupeAndAdd({
  existing,
  incoming,
  path,
  readField,
  mergeObjects,
  offset,
}: DedupeAndAddProps) {
  if (isUndefined(offset) || offset === 0) {
    return incoming;
  }
  const merged = {
    ...incoming,
    [path]: existing ? [...existing[path]] : [],
  };
  const indexes = {};
  if (existing) {
    const existingPath = get(existing, path);
    existingPath?.forEach((item: StoreObject, index: number) => {
      const id = readField<string>('id', item);
      if (id) {
        indexes[id] = index;
      }
    });
  }
  const incomingPath = get(incoming, path);
  const mergedPath = get(merged, path);
  incomingPath?.forEach((item: StoreObject) => {
    const id = readField<string>('id', item);
    if (id) {
      const index = indexes[id];
      if (typeof index === 'number') {
        mergedPath[index] = mergeObjects(mergedPath[index], item);
      } else {
        indexes[id] = mergedPath.length;
        mergedPath.push(item);
      }
    }
  });

  return merged;
}

// Builds typePolicies for workflow component types that should be normalized using
// a composite key of (workflowTemplateId, id). The workflowTemplateId is hidden
// from reads to avoid leaking into application code or mutation variables.
export function createWorkflowComponentTypePolicies(typeNames: string[]): Record<string, any> {
  return typeNames.reduce(
    (acc, typeName) => {
      acc[typeName] = {
        keyFields: false,
        // TODO https://app.shortcut.com/coast/story/61665/use-unique-apollo-cache-keys-for-components-and-view-templates
        // keyFields: (obj: Readonly<StoreObject>, context: Readonly<KeyFieldsContext>) => {
        //   // All components use __cacheKeyWorkflowTemplateId (set by stampWorkflowTemplateIdLink).
        //   // This keeps workflowTemplateId as pure data for components like RelatedCardComponent.
        //   const workflowTemplateId = obj.__cacheKeyWorkflowTemplateId;
        //   if (!isNil(workflowTemplateId)) {
        //     return `${context.typename}:${workflowTemplateId}:${obj.id}`;
        //   }
        //   return false;
        // },
        // fields: {
        //   __cacheKeyWorkflowTemplateId: {
        //     read() {
        //       return undefined;
        //     },
        //   },
        // },
      };
      return acc;
    },
    {} as Record<string, any>,
  );
}
